import React from "react";
import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Link from "@tiptap/extension-link";

// Hotfix-6: Override default rel to remove nofollow from article body links.
// renderHTML strips any lingering "nofollow" saved in pre-Hotfix-6 content JSON
// so internal cross-links and authoritative external links pass equity.
const SeoLink = Link.extend({
  addAttributes() {
    return {
      ...this.parent?.(),
      rel: { default: 'noopener noreferrer' },
    };
  },
  renderHTML({ HTMLAttributes }) {
    // Strip nofollow from the stored rel value (handles legacy content)
    if (typeof HTMLAttributes.rel === 'string') {
      HTMLAttributes.rel = HTMLAttributes.rel
        .split(/\s+/)
        .filter((t: string) => t !== 'nofollow')
        .join(' ') || 'noopener noreferrer';
    }
    return ['a', HTMLAttributes, 0];
  },
});
import HorizontalRule from "@tiptap/extension-horizontal-rule";
import { TableViewerNode } from "./extensions/TableViewerNode";
import { TableRow } from "@tiptap/extension-table-row";
import { TableCell } from "@tiptap/extension-table-cell";
import { TableHeader } from "@tiptap/extension-table-header";
import TaskList from "@tiptap/extension-task-list";
import TaskItem from "@tiptap/extension-task-item";
import Underline from "@tiptap/extension-underline";
import TextAlign from "@tiptap/extension-text-align";
import Superscript from "@tiptap/extension-superscript";
import { ImageGalleryNode } from "./extensions/ImageGalleryNode";
import { PlyrYoutubeNode } from "./extensions/PlyrYoutubeNode";
import { CtaBlockNode } from "./extensions/CtaBlockNode";
import { ReferencesBlockNode } from "./extensions/ReferencesBlockNode";
import { FaqBlockNode } from "./extensions/FaqBlockNode";
import { WaitlistLinkMark } from "./extensions/WaitlistLinkMark";
import { DatawrapperChartNode } from "./extensions/DatawrapperChartNode";
import { ImageNode } from "./extensions/ImageNode";
import { useIsMobile } from "@/hooks/use-mobile";
import { useWaitlistModal } from "@/components/waitlist/WaitlistContext";
import { trackEvent, ANALYTICS_EVENTS } from "@/lib/analytics";
interface PostViewerProps {
  content: any;
}

export default function PostViewer({ content }: PostViewerProps) {
  const isMobile = useIsMobile();
  const { openModal } = useWaitlistModal();
  const containerRef = React.useRef<HTMLDivElement>(null);
  
  // Extract table variants from JSON content
  const tableVariants = React.useMemo(() => {
    if (!content?.content) return [];
    
    const findTableVariants = (nodes: any[]): string[] => {
      const variants: string[] = [];
      for (const node of nodes) {
        if (node.type === 'table') {
          variants.push(node.attrs?.variant || 'row-emphasis');
        }
        if (node.content) {
          variants.push(...findTableVariants(node.content));
        }
      }
      return variants;
    };
    
    return findTableVariants(content.content || []);
  }, [content]);
  
  // After TipTap renders, apply variant classes directly to table elements
  // This is critical because TipTap's Table extension ignores our renderHTML classes
  React.useEffect(() => {
    const container = containerRef.current;
    if (!container) return;
    
    const applyTableVariants = () => {
      // Find ALL table elements in the container, regardless of wrapper structure
      const tables = container.getElementsByTagName('table');
      if (tables.length === 0) return false;
      
      Array.from(tables).forEach((table, index) => {
        const variant = tableVariants[index] || 'row-emphasis';
        
        // Remove any existing variant classes
        table.classList.remove('table-variant-row-emphasis', 'table-variant-column-emphasis', 'table-variant-grid');
        
        // Add the correct classes and attribute
        table.classList.add('table-block', `table-variant-${variant}`);
        table.setAttribute('data-table-variant', variant);
      });
      
      return true;
    };
    
    // Try immediately
    applyTableVariants();
    
    // Use MutationObserver to catch when TipTap renders
    const observer = new MutationObserver((mutations) => {
      // Check if any mutation added a table
      for (const mutation of mutations) {
        if (mutation.type === 'childList') {
          applyTableVariants();
          break;
        }
      }
    });
    
    observer.observe(container, {
      childList: true,
      subtree: true,
    });
    
    // Also use RAF and timeouts as fallbacks for when TipTap hydrates
    requestAnimationFrame(applyTableVariants);
    
    const timers = [0, 50, 100, 250, 500, 1000, 2000].map(delay => 
      setTimeout(applyTableVariants, delay)
    );
    
    return () => {
      observer.disconnect();
      timers.forEach(clearTimeout);
    };
  }, [content, tableVariants]);
  
  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: {
          levels: [1, 2, 3],
        },
        horizontalRule: false,
      }),
      ImageNode,
      PlyrYoutubeNode,
      ImageGalleryNode,
      CtaBlockNode,
      ReferencesBlockNode,
      FaqBlockNode,
      WaitlistLinkMark,
      DatawrapperChartNode,
      SeoLink.configure({
        openOnClick: true,
        HTMLAttributes: {
          class: "text-info underline underline-offset-4 decoration-info/60 hover:decoration-transparent hover:text-info/80 transition-all duration-200",
          target: "_blank",
          rel: "noopener noreferrer",
        },
      }),
      HorizontalRule.configure({
        HTMLAttributes: {
          class: "my-8 border-t border-border",
        },
      }),
      // Table extensions with variant support
      // IMPORTANT: Set resizable=false AND cellMinWidth=0 to prevent the Table
      // extension from using a NodeView wrapper (which breaks renderHTML class output).
      // This ensures the variant classes are applied directly to the <table> element.
      TableViewerNode.configure({
        resizable: false,
        cellMinWidth: 0,
        HTMLAttributes: {
          class: "border-collapse table-auto w-full my-6",
        },
      }),
      TableRow,
      TableCell.configure({
        HTMLAttributes: {
          class: "p-3 text-left",
        },
      }),
      TableHeader.configure({
        HTMLAttributes: {
          class: "p-3 font-semibold text-left",
        },
      }),
      // Task list extensions
      TaskList.configure({
        HTMLAttributes: {
          class: "not-prose pl-0 list-none",
        },
      }),
      TaskItem.configure({
        nested: true,
        HTMLAttributes: {
          class: "flex items-start gap-2 my-1",
        },
      }),
      // Text formatting extensions
      Underline,
      TextAlign.configure({
        types: ["heading", "paragraph"],
      }),
      Superscript,
    ],
    content: content || undefined,
    editable: false,
    editorProps: {
      attributes: {
        // Use prose-article for design system lineage
        // Changes to primitives (e.g., primitive-text-21) propagate here automatically
        class: "prose-article max-w-none focus:outline-none",
      },
    },
  }, [isMobile]); // Re-create editor when mobile state changes

  // Handle clicks on links within article content
  // ⚠️ ANALYTICS: Inline link and waitlist link tracking
  const handleContentClick = (e: React.MouseEvent) => {
    const target = e.target as HTMLElement;

    // Track all anchor link clicks in article body, EXCEPT reference links
    // (Reference links are tracked separately in ReferencesBlockNode)
    const link = target.closest('a');
    const isReferenceLink = link?.closest('.references-block') !== null;

    if (link && link.href && !isReferenceLink) {
      const isExternal = !link.href.includes(window.location.hostname);
      trackEvent(ANALYTICS_EVENTS.INLINE_LINK_CLICKED, {
        link_url: link.href,
        link_text: link.textContent?.slice(0, 100),
        is_external: isExternal,
        article_url: window.location.pathname,
      });
    }

    // Existing waitlist link handling
    if (target.hasAttribute('data-waitlist-link') || target.closest('[data-waitlist-link]')) {
      e.preventDefault();
      openModal('article_waitlist_link');
    }
  };

  if (!content) {
    return (
      <div className="text-muted-foreground text-center py-8">
        No content available.
      </div>
    );
  }

  return (
    <div ref={containerRef} onClick={handleContentClick}>
      <EditorContent editor={editor} />
    </div>
  );
}
